import { api as defaultApi, type ApiClient } from "./client";
import type {
  Anime,
  AnimeInfo,
  AnimeItem,
  EpisodeResponse,
  InfoResponse,
  ListResponse,
  ResolveResponse,
  Stream,
  StreamServer,
} from "./types";

const DEFAULT_PROVIDER = "animeav1";
const BASE = "/api/v1/anime";

function toAnime(item: AnimeItem, fallbackProvider: string): Anime {
  return {
    url: item.url ?? "",
    title: item.title ?? "Sin título",
    imageUrl: item.image ?? "",
    type: item.type ?? "",
    year: item.year ?? "",
    provider: item.provider ?? fallbackProvider,
    providerLabel: item.providerLabel ?? item.provider ?? fallbackProvider,
  };
}

/** El catalogo de la app excluye proveedores para adultos, igual que en Android. */
function isAdult(item: AnimeItem): boolean {
  return `${item.provider ?? ""} ${item.url ?? ""}`.toLowerCase().includes("hentai");
}

export function createAnimeApi(api: ApiClient = defaultApi) {
  return {
    async catalog(page = 1, provider = DEFAULT_PROVIDER, genre?: string): Promise<Anime[]> {
      const res = await api.get<ListResponse>(`${BASE}/catalog`, { provider, page, genre });
      return (res.data?.results ?? []).map((item) => toAnime(item, provider));
    },

    async search(query: string, signal?: AbortSignal, domain?: string): Promise<Anime[]> {
      const res = await api.get<ListResponse>(`${BASE}/search`, { q: query, domain }, signal);
      return (res.data?.results ?? []).filter((item) => !isAdult(item) && item.url).map((item) => toAnime(item, domain || "Multi"));
    },

    async info(url: string): Promise<AnimeInfo> {
      const res = await api.get<InfoResponse>(`${BASE}/info`, { url });
      const d = res.data;
      if (!d) throw new Error("No se encontró información del anime");
      const score = typeof d.score === "number" ? d.score : Number.parseFloat(String(d.score ?? ""));
      return {
        ...toAnime(d, DEFAULT_PROVIDER),
        url,
        description: d.description ?? "Sin sinopsis disponible",
        status: d.status ?? "",
        score: Number.isFinite(score) ? score : null,
        genres: (d.genres ?? []).map((g) => g.name ?? "").filter(Boolean),
        episodes: (d.episodes ?? [])
          .filter((ep) => ep.url)
          .map((ep) => ({
            url: ep.url ?? "",
            number: String(ep.number ?? ""),
            title: ep.title ?? `Capítulo ${ep.number ?? ""}`,
          })),
      };
    },

    async servers(episodeUrl: string): Promise<StreamServer[]> {
      const res = await api.get<EpisodeResponse>(`${BASE}/episode`, { url: episodeUrl });
      const servers = res.data?.servers;
      return [...(servers?.sub ?? []), ...(servers?.dub ?? [])]
        .filter((s) => s.url)
        .map((s) => ({ name: s.server ?? "Servidor", url: s.url ?? "" }));
    },

    async resolve(serverUrl: string): Promise<Stream> {
      const res = await api.get<ResolveResponse>(`${BASE}/resolve`, { url: serverUrl });
      if (!res.streamUrl) throw new Error("No se pudo resolver el enlace de video");
      // El navegador no deja fijar Referer: ese stream daria 403, mejor probar el siguiente servidor.
      if (res.headers && Object.keys(res.headers).length > 0) throw new Error("Servidor no reproducible en el navegador");
      const isHls = res.mediaType === "hls" || res.streamUrl.split("?")[0]?.endsWith(".m3u8") === true;
      return { url: res.streamUrl, server: res.server ?? "Servidor", isHls };
    },

    /** Resuelve hasta 5 embeds en paralelo en el backend descartando los que exigen cabeceras no soportadas en web. */
    async resolveBatch(serverUrls: string[]): Promise<{ stream: Stream; resolvedFrom: string }> {
      const res = await api.get<ResolveResponse>(`${BASE}/resolve`, {
        urls: JSON.stringify(serverUrls.slice(0, 5)),
        webPlayable: 1,
      });
      if (!res.streamUrl || !res.resolvedFrom) throw new Error("No se pudo resolver el enlace de video");
      if (res.headers && Object.keys(res.headers).length > 0) throw new Error("Servidor no reproducible en el navegador");
      const isHls = res.mediaType === "hls" || res.streamUrl.split("?")[0]?.endsWith(".m3u8") === true;
      return {
        stream: { url: res.streamUrl, server: res.server ?? "Servidor", isHls },
        resolvedFrom: res.resolvedFrom,
      };
    },

    /**
     * Prueba los servidores candidatos en lotes paralelos de hasta 5 URLs (con fallback secuencial
     * si un servidor o mock solo implementa `?url=`).
     */
    async firstPlayableStream(servers: StreamServer[], skip: ReadonlySet<string> = new Set()): Promise<{ stream: Stream; server: StreamServer }> {
      const candidates = servers.filter((s) => !skip.has(s.url));
      if (candidates.length === 0) {
        throw new Error("Ningún servidor disponible para este episodio");
      }

      if (candidates.length > 1) {
        for (let i = 0; i < candidates.length; i += 5) {
          const chunk = candidates.slice(i, i + 5);
          try {
            const { stream, resolvedFrom } = await this.resolveBatch(chunk.map((c) => c.url));
            const matched = chunk.find((c) => c.url === resolvedFrom) ?? chunk[0]!;
            return { stream, server: matched };
          } catch {
            // Si falla el lote (o en un entorno mock sin soporte de ?urls=), se cae al intento individual.
          }
        }
      }

      for (const server of candidates) {
        try {
          return { stream: await this.resolve(server.url), server };
        } catch {
          // siguiente servidor
        }
      }
      throw new Error("Ningún servidor disponible para este episodio");
    },

    imageUrl: api.imageUrl,
  };
}

export const animeApi = createAnimeApi();
